import React, { PropsWithChildren } from "react";
import { render } from "@testing-library/react";
import { configureStore, createSlice } from "@reduxjs/toolkit";
import { Provider } from "react-redux";

// As a basic setup, import same slice reducers
import interactionTreeSlice from "../store/slices/InteractionTreeSlice";
import navSlice from "../store/slices/NavSlice";
import authSlice from "../store/slices/AuthSlice";
import type { RenderOptions } from "@testing-library/react";
import type { PreloadedState } from "@reduxjs/toolkit";
import { AppStore, RootState } from "../store/Store";
import { initialState as initialStateNavSlice } from "../store/slices/NavSlice";
import { initialState as initialStateAuthSlice } from "../store/slices/AuthSlice";
// import { generateTestInteractionTreeSliceInitialState } from "../TestData";
import { stateDeepCopy } from "./InteractionTreeHelper";
// import patientSlice from "../store/slices/PatientSlice";
import { getDBToken, runDatabaseFunction } from "./HttpHelper";
import Test from "../components/test/Test";

// const initialTestState = generateTestInteractionTreeSliceInitialState();
const initialTestState = {};

const omittedTests = ["clear_test_schema","get_test_functions", "test_run_all"]

// This type interface extends the default options for render from RTL, as well
// as allows the user to specify other things such as initialState, store.
interface ExtendedRenderOptions extends Omit<RenderOptions, "queries"> {
  preloadedState?: PreloadedState<RootState> | any;
  store?: AppStore;
}

// for tests, we need to override the inital value of breadcrumbs
// because we want to emulate interaction #1 already being selected on render
const initialStateNavOverride = stateDeepCopy(initialStateNavSlice);
initialStateNavOverride.breadcrumbs = [
  {
    id: 1,
    type: "pathway",
    title: "TCH Total Joint",
    properties: {},
    modified: "2023-04-20T17:10:22.063963",
  },
];

// make preloaded state setup available app-wide for testing purposes
export const standardPreloadedState = {
  interactionTree: initialTestState,
  nav: initialStateNavOverride,
  auth: initialStateAuthSlice,
};

// create test store with default reducers
export function configureTestStore(
  preloadedState: any = standardPreloadedState,
) {
  return configureStore({
    reducer: {
      interactionTree: interactionTreeSlice.reducer,
      nav: navSlice.reducer,
      auth: authSlice.reducer,
      patients: patientSlice.reducer,
    },
    preloadedState,
  });
}

// wraps a provided child with the storefor testing
export function renderWithProviders(
  ui: React.ReactElement,
  {
    preloadedState = standardPreloadedState,
    // Automatically create a store instance if no store was passed in
    store = configureTestStore(preloadedState),
    ...renderOptions
  }: ExtendedRenderOptions = {},
) {
  function Wrapper({ children }: PropsWithChildren<any>): JSX.Element {
    return <Provider store={store}>{children}</Provider>;
  }

  // Return an object with the store and all of RTL's query functions
  return { store, ...render(ui, { wrapper: Wrapper, ...renderOptions }) };
}

// for stories
// dont need full reducers for stories
export const InteractionTestStore = ({ children }: any) => {
  const interactionTreeTestSlice = createSlice({
    name: "interactionTree",
    initialState: initialTestState,
    reducers: {},
  });

  const navTestSlice = createSlice({
    name: "nav",
    initialState: initialStateNavSlice,
    reducers: {},
  });

  const authTestSlice = createSlice({
    name: "auth",
    initialState: initialStateAuthSlice,
    reducers: {},
  });

  return (
    <Provider
      store={configureStore({
        reducer: {
          interactionTree: interactionTreeTestSlice.reducer,
          nav: navTestSlice.reducer,
          auth: authTestSlice.reducer,
          patients: patientSlice.reducer,
        },
      })}
    >
      {children}
    </Provider>
  );
};

export const getTestFunctions = async () => {
  let testFunctions: any = {};
  const dbFunc = "get_test_functions";
  try {
    testFunctions = await runDatabaseFunction(dbFunc, {})
    // TODO - NEED TO UPDATE THE NODES IN REDUX AS WELL, IN A SIMILAR WAY
  } catch (err: any) {
    console.error("Error with get_test_functions: " + err);
    return {}
  }
  testFunctions = await testFunctions.json()
  return testFunctions
}

export interface TestFunctionProps {
  function_name: string;
  function_arguments: string;
}


interface KeyValuePair {
  key: string;
  dataType: string;
  value: any
}

export const getFunctionArgsFromTestString = (input: string): KeyValuePair[] => {
  // Split the input string by commas
  const parts = input.split(',');

  // Create an array to store the key-value pairs
  const resultArray: KeyValuePair[] = [];

  // Iterate over each part
  parts.forEach(part => {
    // Trim the part to remove any leading or trailing whitespace
    const trimmedPart = part.trim();
    // Split the trimmed part by the first space
    const [key, ...rest] = trimmedPart.split(' ');
    // Join the rest of the parts back to form the value
    const dataType = rest.join(' ');
    const value = ""; // set this to empty for now. The user will populate if needed.
    // Add the key-value pair to the array
    resultArray.push({ key, dataType, value });
  });

  return resultArray;
}

interface OutputFormat {
  [key: string]: string;
}
export const formatArgsForPostgresProcessing = (args: Array<KeyValuePair>) => {
  const resultObject: OutputFormat = {};

  // Iterate over each item in the input array
  args.forEach(item => {
    // Add the key-value pair to the result object
    resultObject[item.key] = item.value;
  });

  return resultObject;
}


export const runTest = async (funcName: string, args: Array<KeyValuePair>) => {
  let result: any = {};
  try {
    result = await runDatabaseFunction(funcName, {})
    // TODO - NEED TO UPDATE THE NODES IN REDUX AS WELL, IN A SIMILAR WAY
  } catch (err: any) {
    console.error("Error with get_test_functions: " + err);
    return {}
  }
  result = await result.json()
  return result


}


export const convertFunctionNameToDescription = (input: string) => {
  // Split the input string by underscores
  const words = input.split('_');

  // Capitalize the first word
  const capitalizedWords = words.map((word, index) => {
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }
    return word;
  });

  // Join the words with spaces
  const sentence = capitalizedWords.join(' ');

  // Add a period at the end of the sentence
  return sentence + '.';
}


export const returnTestsTSX = (individualTests: Array<TestFunctionProps>) => {
  individualTests = individualTests.filter((ift)=>{
    return !omittedTests.includes(ift.function_name)
  })
  const individualFunctionsTSX = individualTests.map((individualFunction: TestFunctionProps) => {
    const function_name = individualFunction.function_name
    let function_arguments: any = {}
    if (individualFunction.function_arguments != 'No arguments') {
      function_arguments = getFunctionArgsFromTestString(individualFunction.function_arguments);
      function_arguments = formatArgsForPostgresProcessing(function_arguments)
    }

    return (
      <Test 
        key={function_name} 
        individualFunction={individualFunction} 
        testFunction={async ()=>{
         await runTestFunction(function_name,function_arguments)
        }} 
      />
    )

  })

  return individualFunctionsTSX;
}


export const runTestFunction = async (
  function_name: string,
  parameters: any,
): Promise<Response> => {
  const url = process.env.REACT_APP_API_URL + "/rpc/" + function_name;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + getDBToken(),
        "Content-Type": "application/json",
        "Content-Profile": "test",
      },
      body: JSON.stringify(parameters),
    });

    if (!response.ok) {
      // Handle HTTP errors
      const errorData = await response.json();
      throw new Error(`Error ${response.status}: ${errorData.message}`);
    }
  
    return response;
  } catch (error) {
    // Handle network or other errors
    console.error("Error in runTestFunction:", error);

    // Optionally, you can return a custom response or rethrow the error
    // return new Response(JSON.stringify({ error: error.message }), { status: 500 });
    throw error;
  }



};